import { supabase } from "@/lib/supabase";
import { formatStorageUrl } from "@/lib/supabase";
import { Song } from "@/types/player";
import { cacheService, CACHE_KEYS } from '@/services/cacheService';
import { removeAccents, normalizeForSearch } from '@/utils/stringUtils';
import { formatSong } from '@/utils/songUtils';

// TTL do cache: 5 minutos
const CACHE_TTL = 5 * 60 * 1000;

interface UserData {
  id: string;
  tipo_de_conta?: string;
}

// Helper function to filter out duplicate songs
export const removeDuplicateSongs = (songs: Song[]): Song[] => {
  const seen = new Set();
  return songs.filter(song => {
    const key = `${song.livro}-${song.capitulo}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
      return true;
  });
};

// Função para buscar uma música por ID com cache e retry
export const getSongById = async (id: number): Promise<Song | null> => {
  const cacheKey = `song_detail_${id}`;
  const cached = await cacheService.get<Song>(cacheKey);
  
  if (cached) {
    return cached;
  }

  // Retry logic
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      const { data: song, error } = await supabase
        .from('songs')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      if (!song) return null;

      const formattedSong = formatSong(song);

      // Armazena no cache com TTL
      await cacheService.set(cacheKey, formattedSong, CACHE_TTL);

      return formattedSong;
    } catch (error) {
      if (attempt === 3) {
        console.error(`Erro ao buscar música ${id} após 3 tentativas:`, error);
        return null;
      }
      // Aguarda antes da próxima tentativa
      await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
    }
  }
  
  return null;
};

// Função para buscar músicas por livro bíblico com cache
export const getSongsByBook = async (book: string): Promise<Song[]> => {
  const cacheKey = `songs_book_${book}`;
  const cached = await cacheService.get<Song[]>(cacheKey);
  
  if (cached && cached.length > 0) {
    return cached;
  }

  try {
    const { data: songs, error } = await supabase
      .from('songs')
      .select('*')
      .eq('livro', book)
      .order('capitulo', { ascending: true });

    if (error) throw error;

    const formattedSongs = songs.map(song => formatSong(song));

    // Armazena no cache com TTL
    await cacheService.set(cacheKey, formattedSongs, CACHE_TTL);

    return formattedSongs;
  } catch (error) {
    console.error('Erro ao buscar músicas por livro:', error);
    return [];
  }
};

// Função para buscar músicas por capítulo com cache
export const getSongsByChapter = async (book: string, chapter: number): Promise<Song[]> => {
  const cacheKey = `songs_chapter_${book}_${chapter}`;
  const cached = await cacheService.get<Song[]>(cacheKey);
  
  if (cached && cached.length > 0) {
    return cached;
  }

  try {
    const { data: songs, error } = await supabase
      .from('songs')
      .select('*')
      .eq('livro', book)
      .eq('capitulo', chapter)
      .order('id', { ascending: true });

    if (error) throw error;

    const formattedSongs = songs.map(song => formatSong(song));

    // Armazena no cache com TTL
    await cacheService.set(cacheKey, formattedSongs, CACHE_TTL);

    return formattedSongs;
  } catch (error) {
    console.error('Erro ao buscar músicas por capítulo:', error);
    return [];
  }
};

// Função para buscar todas as músicas com cache global
export const getAllSongs = async (): Promise<Song[]> => {
  const cacheKey = 'get_all_songs';
  const cached = await cacheService.get<Song[]>(cacheKey);
  
  if (cached && cached.length > 0) {
    return cached;
  }

  try {
    const { data: songs, error } = await supabase
      .from('songs')
      .select('*')
      .order('id', { ascending: true });

    if (error) throw error;

    const formattedSongs = songs.map(song => formatSong(song));

    // Armazena no cache com TTL
    await cacheService.set(cacheKey, formattedSongs, CACHE_TTL);

    return formattedSongs;
  } catch (error) {
    console.error('Erro ao buscar todas as músicas:', error);
    return [];
  }
};

// Função para buscar músicas com cache de busca (TTL: 2 minutos)
export const searchSongs = async (query: string): Promise<Song[]> => {
  if (!query.trim()) return [];
  
  const cacheKey = `search_${query.toLowerCase().trim()}`;
  
  // TTL específico para buscas: 2 minutos
  const SEARCH_CACHE_TTL = 2 * 60 * 1000;
  const cached = await cacheService.get<Song[]>(cacheKey);
  
  if (cached && cached.length > 0) {
    return cached;
  }

  try {
    // Sanitiza a query para evitar problemas com SQL injection
    const sanitizedQuery = query.replace(/[%_]/g, '\\$&');
    const searchPattern = `%${sanitizedQuery}%`;
    
    // Usa textSearch para melhor performance e resultados
    const { data: songs, error } = await supabase
      .from('songs')
      .select('*')
      .or(`descricao.ilike.${searchPattern},livro.ilike.${searchPattern},capitulo::text.ilike.${searchPattern},versiculos.ilike.${searchPattern}`)
      .order('is_free', { ascending: false })
      .order('id', { ascending: true })
      .limit(50);

    if (error) {
      // Se der erro na busca, tenta buscar todas as músicas e filtra localmente
      const allSongs = await getAllSongs();
      const normalizedQuery = normalizeForSearch(sanitizedQuery);
      
      const filteredSongs = allSongs.filter(song => {
        const desc = normalizeForSearch(song.descricao || '');
        const livro = normalizeForSearch(song.livro || '');
        const vers = normalizeForSearch(song.versiculos || '');
        const cap = String(song.capitulo || '').toLowerCase();
        
        return desc.includes(normalizedQuery) ||
               livro.includes(normalizedQuery) ||
               vers.includes(normalizedQuery) ||
               cap.includes(normalizedQuery);
      });
      
      // Cache os resultados do fallback com TTL específico
      await cacheService.set(cacheKey, filteredSongs, SEARCH_CACHE_TTL);
      
      return filteredSongs;
    }

    const formattedSongs = (songs || []).map(song => formatSong(song));

    // Armazena no cache com TTL específico
    await cacheService.set(cacheKey, formattedSongs, SEARCH_CACHE_TTL);

    return formattedSongs;
  } catch (error) {
    // Em caso de erro, tenta buscar todas as músicas e filtra localmente
    try {
      const allSongs = await getAllSongs();
      const normalizedQuery = normalizeForSearch(query);
      
      const filteredSongs = allSongs.filter(song => {
        const desc = normalizeForSearch(song.descricao || '');
        const livro = normalizeForSearch(song.livro || '');
        const vers = normalizeForSearch(song.versiculos || '');
        const cap = String(song.capitulo || '').toLowerCase();
        
        return desc.includes(normalizedQuery) ||
               livro.includes(normalizedQuery) ||
               vers.includes(normalizedQuery) ||
               cap.includes(normalizedQuery);
      });

      return filteredSongs;
    } catch (fallbackError) {
    return [];
    }
  }
};
